/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { ButtonComp, TextInputComp } from "../../Ui";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { Modal, Button } from "react-bootstrap";
import { Formik, Form } from "formik";
import { useResetPasswordMutation } from "../../../store/auth/authApi";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { selectCurrentCoopData } from "../../../store/auth";
import { useUser } from "../../../hooks";

export default function ResetPasswordForm({ setPageName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetPassword] = useResetPasswordMutation();
  const coopData = useSelector(selectCurrentCoopData);
  const user = useUser();
  
  // Get company data - prefer coopData (from domain fetch) or user company data
  const companyData = coopData || user?.company;
  const companyName = companyData?.name || "";
  const companyAddress = companyData?.address || "";
  const companyLogo = companyData?.logo_url || "../Images/Login/sign-up-image.png";

  const initialValues = {
    newPassword: "",
    confirmPassword: "",
  };

  // Yup validations schema
  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  // Check if user came from OTP verification
  useEffect(() => {
    if (!location.state?.otpVerified) {
      // Redirect to forgot password if not verified
      navigate("/forgot-password");
    }
  }, [location.state, navigate]);

  async function handleResetPassword(values) {
    const { email, otp, userInfo, companyId } = location.state || {};

    if (!email || !otp || !userInfo || !companyId) {
      toast.error("Missing required information. Please start the password reset process again.");
      navigate("/forgot-password");
      return;
    }

    // Get user ID from userInfo
    const userId = userInfo?.id || userInfo?.staff_id || userInfo?.member_id;
    if (!userId) {
      toast.error("User information is invalid. Please start the password reset process again.");
      navigate("/forgot-password");
      return;
    }

    setIsLoading(true);

    try {
      const response = await resetPassword({
        companyId: companyId,
        userId: userId,
        email: email,
        otp: otp,
        password: values.newPassword,
      });

      if (response.error) {
        const errorMessage =
          response.error?.data?.message ||
          response.error?.data?.error ||
          "Failed to reset password. Please try again.";
        toast.error(errorMessage);
        return;
      }

      // Show success modal
      setShowModal(true);
      toast.success("Password has been reset successfully");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  const handleSuccess = () => {
    setShowModal(false);
    navigate("/login");
  };

  return (
    <div className="w-full min-w-0 max-w-[480px] bg-white py-6 px-4 sm:py-8 sm:px-6 md:px-8 lg:py-10 lg:px-10 rounded-2xl sm:rounded-3xl flex flex-col relative shadow-sm sm:shadow-md">
      <div className="absolute -top-12 -right-5">
        <svg
          width="34"
          height="32"
          viewBox="0 0 34 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.0001 0.200348L20.8884 12.1674H33.4713L23.2915 19.5635L27.1799 31.5305L17.0001 24.1345L6.82026 31.5305L10.7086 19.5635L0.528801 12.1674H13.1117L17.0001 0.200348Z"
            fill="var(--color-primary)"
          />
        </svg>
      </div>
      <div className="absolute top-0 -right-10">
        <svg
          width="24"
          height="22"
          viewBox="0 0 24 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M23.7024 0.128797L14.3185 21.944L0.117944 2.90972L23.7024 0.128797Z"
            fill="#B13E4A"
          />
        </svg>
      </div>

      {companyName && (
        <div className="flex flex-col items-center bg-white rounded-3xl text-center lg:hidden">
          <img
            src={companyLogo}
            alt={companyName}
            className="w-[100px] h-[100px] rounded-full"
          />
          <p className="font-[700] text-[32px]">{companyName}</p>
          {companyAddress && (
            <p className="text-[#858585] text-lg">{companyAddress}</p>
          )}
        </div>
      )}

      <div className="text-left">
        <h2 className="font-[700] mb-2 text-[24px]">Reset Password</h2>
        <div className="">Create new password for your cooperative login </div>
      </div>

      <div className="mb-3">
        <div></div>
      </div>

      {/* Success Modal */}
      <Modal show={showModal} centered onHide={() => setShowModal(false)}>
        <Modal.Body className="p-5">
          <div className="flex flex-col gap-3 items-center justify-center">
            <div className="text-[100px]">
              <svg
                width="100"
                height="101"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_5705_35917)">
                  <path
                    d="M100 50.5C100 78.1139 77.6139 100.5 50 100.5C22.3861 100.5 0 78.1139 0 50.5C0 22.8861 22.3861 0.5 50 0.5C77.6139 0.5 100 22.8861 100 50.5Z"
                    fill="#FFCC4D"
                  />
                  <path
                    d="M49.9999 61.1917C41.761 61.1917 36.2916 60.2306 29.5305 58.9167C27.9832 58.6222 24.9805 58.9167 24.9805 63.4667C24.9805 72.5639 35.4332 83.9361 49.9999 83.9361C64.5666 83.9361 75.0194 72.5639 75.0194 63.4667C75.0194 58.9167 72.0138 58.6194 70.4694 58.9167C63.7082 60.2306 58.2416 61.1917 49.9999 61.1917Z"
                    fill="#664500"
                  />
                  <path
                    d="M46.25 9.61389C45.3381 7.023 43.6267 4.78879 41.3627 3.23361C39.0987 1.67842 36.3992 0.882671 33.6537 0.961168C30.9081 1.03967 28.2585 1.98835 26.087 3.67035C23.9156 5.35235 22.3346 7.68069 21.5723 10.3194C19.8849 9.1375 17.94 8.37482 15.899 8.09464C13.858 7.81445 11.7796 8.02483 9.83614 8.70833C6.59343 9.85371 3.93824 12.2399 2.45424 15.3423C0.970234 18.4446 0.778886 22.0094 1.92225 25.2528C2.26114 26.2083 2.71114 27.0944 3.23337 27.9139C8.65281 37.8639 24.3111 44.8583 33.7917 44.9444C41.1167 38.925 48.9195 23.6528 46.8973 12.5083C46.7919 11.5221 46.5747 10.5511 46.25 9.61389ZM53.75 9.61389C54.8949 6.37067 57.2808 3.71483 60.3832 2.23028C63.4857 0.745724 67.0507 0.553988 70.2945 1.69722C72.2382 2.38447 73.9902 3.5251 75.4051 5.02456C76.82 6.52403 77.8572 8.33914 78.4306 10.3194C80.118 9.1375 82.0628 8.37482 84.1038 8.09464C86.1448 7.81445 88.2232 8.02483 90.1667 8.70833C93.4095 9.85377 96.0648 12.2398 97.5493 15.342C99.0337 18.4443 99.2258 22.009 98.0834 25.2528C97.7475 26.1867 97.3067 27.0794 96.7695 27.9139C91.35 37.8639 75.6917 44.8583 66.2084 44.9444C58.8834 38.925 51.0834 23.6528 53.1056 12.5083C53.2084 11.5417 53.4167 10.5694 53.75 9.61389Z"
                    fill="#DD2E44"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_5705_35917">
                    <rect
                      width="100"
                      height="100"
                      fill="white"
                      transform="translate(0 0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <p className="font-[700] text-[24px] text-center flex flex-col md:gap-2 md:flex-row">
              <p>Password Reset Successful</p>
              <div className="flex justify-center md:mt-[5px]">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 23.25C6.07 23.25 1.25 18.43 1.25 12.5C1.25 6.57 6.07 1.75 12 1.75C17.93 1.75 22.75 6.57 22.75 12.5C22.75 18.43 17.93 23.25 12 23.25ZM12 3.25C6.9 3.25 2.75 7.4 2.75 12.5C2.75 17.6 6.9 21.75 12 21.75C17.1 21.75 21.25 17.6 21.25 12.5C21.25 7.4 17.1 3.25 12 3.25Z"
                    fill="#19BC31"
                  />
                  <path
                    d="M10.5804 16.0801C10.3804 16.0801 10.1904 16.0001 10.0504 15.8601L7.22043 13.0301C6.93043 12.7401 6.93043 12.2601 7.22043 11.9701C7.51043 11.6801 7.99043 11.6801 8.28043 11.9701L10.5804 14.2701L15.7204 9.1301C16.0104 8.8401 16.4904 8.8401 16.7804 9.1301C17.0704 9.4201 17.0704 9.9001 16.7804 10.1901L11.1104 15.8601C10.9704 16.0001 10.7804 16.0801 10.5804 16.0801Z"
                    fill="#19BC31"
                  />
                </svg>
              </div>
            </p>
            <p className="text-[#858585] text-center">
              Your password has been successfully reset. You can now login with
              your new password.
            </p>
            <button
              className="bg-[var(--color-primary)] w-full py-3 rounded-2xl text-[16px] text-white font-[600]"
              onClick={handleSuccess}
            >
              Proceed to login
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleResetPassword}
      >
        <Form>
          <div className="col-lg-11 w-full">
            <div className="mb-5">
              <TextInputComp
                LabelClassName={"fw-normal"}
                borderColor={"#A1A0A0"}
                borderWidth="1px"
                InputClassName={"border  border-1 py-2"}
                labelText={"New Password"}
                placeholder="Enter new password"
                name="newPassword"
                type="password"
              />
            </div>
            <div className="mb-5">
              <TextInputComp
                LabelClassName={"fw-normal"}
                borderColor={"#A1A0A0"}
                borderWidth="1px"
                InputClassName={"border  border-1 py-2"}
                labelText={"Confirm Password"}
                placeholder="Confirm new password"
                name="confirmPassword"
                type="password"
              />
            </div>

            <div className="mb-4">
              <ButtonComp
                btnText={isLoading ? "Resetting..." : "Reset Password"}
                type="submit"
                btnClassName="w-100 bg-primary1 text-white border-0 py-3 rounded-4 fw-bold disabled:opacity-50"
                disabled={isLoading}
              />
            </div>

            <div className="mb-4">
              <ButtonComp
                BorderColor={"var(--color-primary)"}
                BorderWidth={"1.5px"}
                btnText={"Back to Login"}
                onClick={() => navigate("/login")}
                btnClassName="w-100 text-primary1   py-3 rounded-4 fw-bold"
                disabled={isLoading}
              />
            </div>
          </div>
        </Form>
      </Formik>
    </div>
  );
}
